import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import ec2InstanceService from '../services/ec2InstanceService';

const EC2InstanceStatusContext = createContext();
const EC2_STATUS_MAX_PROJECTS_PER_POLL = 8;

const uniqueProjectIds = (projectIds = []) => (
  Array.from(new Set(
    projectIds
      .map(projectId => String(projectId ?? '').trim())
      .filter(Boolean)
  ))
);

export const useEC2InstanceStatus = () => {
  const context = useContext(EC2InstanceStatusContext);
  if (!context) {
    throw new Error('useEC2InstanceStatus must be used within an EC2InstanceStatusProvider');
  }
  return context;
};

export const EC2InstanceStatusProvider = ({ children }) => {
  // Store status for each project: { projectId: { state: 'running'|'stopped'|'stopping'|'unknown', ... } }
  const [instanceStatuses, setInstanceStatuses] = useState({});
  // Track projects that are known to have no instance (to skip polling) - use ref for immediate access
  const projectsWithoutInstancesRef = useRef(new Set());
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef(null);
  const projectsRef = useRef([]);
  const isPollingInProgressRef = useRef(false); // Prevent multiple simultaneous polls
  const nextPollStartIndexRef = useRef(0);
  const pollingInterval = 20000; // 20 seconds

  /**
   * Get the status for a specific project
   * @param {string} projectId - Project ID
   * @returns {Object|null} Status object or null if not found
   */
  const getInstanceStatus = (projectId) => {
    return instanceStatuses[projectId] || null;
  };

  /**
   * Check if instance is running for a project
   * @param {string} projectId - Project ID
   * @returns {boolean} True if running
   */
  const isInstanceRunning = (projectId) => {
    const status = instanceStatuses[projectId];
    return status?.state === 'running';
  };

  /**
   * Check if instance is stopped or stopping for a project
   * @param {string} projectId - Project ID
   * @returns {boolean} True if stopped or stopping
   */
  const isInstanceStopped = (projectId) => {
    const status = instanceStatuses[projectId];
    return status?.state === 'stopped' || status?.state === 'stopping';
  };

  /**
   * Check if instance has an error for a project
   * @param {string} projectId - Project ID
   * @returns {boolean} True if there's an error
   */
  const hasInstanceError = (projectId) => {
    const status = instanceStatuses[projectId];
    return status?.state === 'error';
  };

  /**
   * Update status for a single project
   */
  const updateProjectStatus = (projectId, statusData) => {
    setInstanceStatuses(prev => ({
      ...prev,
      [projectId]: statusData
    }));
  };

  /**
   * Poll status for all projects
   */
  const pollAllProjectsStatus = async (projectIds) => {
    if (!projectIds || projectIds.length === 0) {
      return;
    }

    // Prevent multiple simultaneous polls
    if (isPollingInProgressRef.current) {
      return;
    }

    isPollingInProgressRef.current = true;

    try {
      // Filter out projects that are known to have no instances (use ref for immediate access)
      const pollableProjectIds = uniqueProjectIds(projectIds)
        .filter(projectId => !projectsWithoutInstancesRef.current.has(projectId));
      const pollStartIndex = pollableProjectIds.length > 0
        ? nextPollStartIndexRef.current % pollableProjectIds.length
        : 0;
      const projectsToPoll = [
        ...pollableProjectIds.slice(pollStartIndex),
        ...pollableProjectIds.slice(0, pollStartIndex)
      ].slice(0, EC2_STATUS_MAX_PROJECTS_PER_POLL);
      nextPollStartIndexRef.current =
        pollableProjectIds.length > 0
          ? (pollStartIndex + projectsToPoll.length) % pollableProjectIds.length
          : 0;
      
      if (projectsToPoll.length === 0) {
        // Silent skip - no log needed if all projects are filtered
        return;
      }
      
      // Silent polling - only log on significant events (reduced log noise)
    
    // Poll only projects that might have instances
    const statusPromises = projectsToPoll.map(async (projectId) => {
      // Double-check before making the API call (in case another parallel call already detected no instance)
      if (projectsWithoutInstancesRef.current.has(projectId)) {
        return {
          projectId,
          status: null,
          hasNoInstance: true
        };
      }
      
      try {
        const statusResponse = await ec2InstanceService.checkInstanceStatus(projectId);
        
        // Ensure we have a valid response object
        if (!statusResponse || typeof statusResponse !== 'object') {
          return {
            projectId,
            status: {
              state: 'error',
              instanceName: null,
              instanceId: null,
              error: 'Invalid response',
              message: 'Can\'t find instance match to project',
              lastChecked: new Date().toISOString()
            },
            hasNoInstance: false
          };
        }
        
        // Check if instance exists (not just an error about no instance configured)
        if (!statusResponse.success && statusResponse.error === 'No instance name found for project') {
          // Mark this project as having no instance to skip future polls (use ref for immediate effect)
          projectsWithoutInstancesRef.current.add(projectId);
          // Remove from statuses if no instance configured
          return {
            projectId,
            status: null, // null means no instance configured, don't show indicator
            hasNoInstance: true
          };
        }
        
        // If we successfully got a status, make sure it's not in the "no instance" set
        if (statusResponse.success && statusResponse.status) {
          // Remove from no-instance set if we got a valid status
          projectsWithoutInstancesRef.current.delete(projectId);
          
          // Extract state from status response
          const state = statusResponse.status.state || 'unknown';
          
          return {
            projectId,
            status: {
              state,
              instanceName: statusResponse.instance_name || null,
              instanceId: statusResponse.instance_id || null,
              isHealthy: statusResponse.status.is_healthy || false,
              readyForStreaming: statusResponse.ready_for_streaming || false,
              lastChecked: new Date().toISOString()
            },
            hasNoInstance: false
          };
        }
        
        // If we have an error but not "no instance name found", it's an error finding the instance
        // Show warning indicator
        return {
          projectId,
          status: {
            state: 'error',
            instanceName: statusResponse.instance_name || null,
            instanceId: statusResponse.instance_id || null,
            error: statusResponse.error || 'Unknown error',
            message: statusResponse.message || 'Can\'t find instance match to project',
            lastChecked: new Date().toISOString()
          },
          hasNoInstance: false
        };
      } catch (error) {
        console.error(`❌ Error checking status for project ${projectId}:`, error);
        // On network/request error, show warning indicator
        return {
          projectId,
          status: {
            state: 'error',
            instanceName: null,
            instanceId: null,
            error: error.message || 'Network error',
            message: 'Can\'t find instance match to project',
            lastChecked: new Date().toISOString()
          },
          hasNoInstance: false // Don't mark as no instance on network errors
        };
      }
    });

    const results = await Promise.all(statusPromises);
    
    // Update statuses (only include projects with instances)
    setInstanceStatuses(prev => {
      const newStatuses = { ...prev };
      results.forEach(({ projectId, status, hasNoInstance }) => {
        if (status === null) {
          // Remove from statuses if no instance
          delete newStatuses[projectId];
        } else {
          // Update status
          newStatuses[projectId] = status;
        }
      });
      return newStatuses;
    });
    
    } finally {
      // Always release the lock
      isPollingInProgressRef.current = false;
    }
  };

  /**
   * Start polling for given project IDs
   */
  const startPolling = (projectIds) => {
    if (pollIntervalRef.current) {
      // Don't reset cache - just update the project list
      projectsRef.current = uniqueProjectIds(projectIds);
      return;
    }

    projectsRef.current = uniqueProjectIds(projectIds);
    nextPollStartIndexRef.current = 0;
    setIsPolling(true);
    // Only reset cache when starting fresh (first time)
    resetNoInstanceCache();

    // Poll immediately
    pollAllProjectsStatus(projectsRef.current);

    // Set up interval polling
    pollIntervalRef.current = setInterval(() => {
      pollAllProjectsStatus(projectsRef.current);
    }, pollingInterval);
  };

  /**
   * Stop polling
   */
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsPolling(false);
  };

  /**
   * Update project list and continue polling (interval will handle the polling)
   */
  const updateProjects = (projectIds) => {
    projectsRef.current = uniqueProjectIds(projectIds);
    // Don't poll immediately - let the interval handle it to avoid duplicate polls
  };
  
  /**
   * Reset the "no instance" cache (useful when projects are refreshed)
   */
  const resetNoInstanceCache = () => {
    projectsWithoutInstancesRef.current = new Set();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const value = {
    instanceStatuses,
    isPolling,
    getInstanceStatus,
    isInstanceRunning,
    isInstanceStopped,
    hasInstanceError,
    updateProjectStatus,
    startPolling,
    stopPolling,
    updateProjects
  };

  return (
    <EC2InstanceStatusContext.Provider value={value}>
      {children}
    </EC2InstanceStatusContext.Provider>
  );
};

